import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  OnDestroy,
  OnInit, inject, DestroyRef, computed, effect, signal
} from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
} from '@angular/router';
import {AsyncPipe, NgClass, NgStyle} from '@angular/common';
import {combineLatest, Subscription} from 'rxjs';

import {LayoutService} from '../../../../core/services/layout.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {AuthService} from '../../../../core/services/auth.service';
import {SafeLoginDetails} from '../../../../core/models/safe-login-details';

export interface NavItem {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  subItems?: {
    name: string;
    path: string;
    pro?: boolean;
    new?: boolean;
  }[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, AsyncPipe, NgClass, NgStyle],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppSidebarComponent implements OnInit, OnDestroy {

  public readonly layoutService = inject(LayoutService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);

  userDetails!: SafeLoginDetails | null;


  /* =====================
     MENU DATA
  ====================== */
  navItems: NavItem[] = [
    {
      icon: '📂',
      name: 'Category',
      path: '/admin/category',
    },
    {
      icon: '🛍️',
      name: 'Products',
      path: '/admin/products',
    },
    {
      icon: '📦',
      name: 'Orders',
      path: '/admin/orders',
    },
    {
      icon: '📩',
      name: 'Enquiries',
      path: '/admin/enquiries',
    }
  ];


  othersItems: NavItem[] = [ /* SAME DATA – unchanged */];


  isActive(path: string): boolean {
    return this.router.isActive(path, {
      paths: 'subset',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  /* =====================
     UI STATE
  ====================== */
  openSubmenu: string | null = null;
  subMenuHeights: Record<string, number> = {};

  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$ = this.layoutService.isExpanded$;
  readonly isMobileOpen$ = this.layoutService.isMobileOpen$;
  readonly isHovered$ = this.layoutService.isHovered$;

  private subscription = new Subscription();

  ngOnInit(): void {

    // Existing logic (unchanged)
    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(event.urlAfterRedirects);
        }
      });

    this.setActiveMenuFromRoute(this.router.url);
  }

  /* =====================
     UI EVENTS
  ====================== */

  onMouseEnter() {
    if (window.innerWidth >= 1200) {
      this.layoutService.setHovered(true);
    }
  }

  onMouseLeave() {
    if (window.innerWidth >= 1200) {
      this.layoutService.setHovered(false);
    }
  }

  toggleSubmenu(section: string, index: number): void {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
      return;
    }

    this.openSubmenu = key;

    queueMicrotask(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
        this.cdr.markForCheck();
      }
    });
  }

  onSubmenuClick(): void {
    this.layoutService.setMobileOpen(false);
  }

  /* =====================
     ROUTE HANDLING
  ====================== */

  private setActiveMenuFromRoute(url: string): void {
    const groups = [
      {items: this.navItems, prefix: 'main'},
      {items: this.othersItems, prefix: 'others'},
    ];

    for (const group of groups) {
      group.items.forEach((item, index) => {
        item.subItems?.forEach(sub => {
          if (sub.path === url) {
            const key = `${group.prefix}-${index}`;
            this.openSubmenu = key;

            queueMicrotask(() => {
              const el = document.getElementById(key);
              if (el) {
                this.subMenuHeights[key] = el.scrollHeight;
                this.cdr.markForCheck();
              }
            });
          }
        });
      });
    }
  }

  closeSidebar(): void {
    this.layoutService.setMobileOpen(false);
  }

  ngOnDestroy() {

  }

}
